import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Container, Card, Grid, Divider, Chip, useTheme, useMediaQuery } from "@mui/material";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import BuildIcon from "@mui/icons-material/Build";
import EngineeringIcon from "@mui/icons-material/Engineering";
import StraightenIcon from "@mui/icons-material/Straighten";
import TuneIcon from "@mui/icons-material/Tune";
import WhatsAppFloat from "components/WhatsAppFloat";

import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import Breadcrumbs from "examples/Breadcrumbs";

import ProductsCarousel from "./ProductsCarousel";
import ServicesAccordions from "./ServicesAccordions";

import bgBanner from "assets/images/bg-presentation.jpg";
import produtoVideo from "assets/videos/produto-video.mp4";

/* imagens */
import img01_1 from "assets/images/produtoseservicos/01-1.jpg";
import img02_2 from "assets/images/produtoseservicos/02-2.jpg";
import img03_1 from "assets/images/produtoseservicos/03-1.jpg";
import img05_05 from "assets/images/produtoseservicos/05-05.jpg";
import img06_06 from "assets/images/produtoseservicos/06-06.jpg";
import img011 from "assets/images/produtoseservicos/011.jpg";
import img15 from "assets/images/produtoseservicos/15.jpg";
import img17 from "assets/images/produtoseservicos/17.jpg";
import img032 from "assets/images/produtoseservicos/032.jpg";
import imgDour from "assets/images/produtoseservicos/dour.jpg";
import imgIm5 from "assets/images/produtoseservicos/im5.jpg";
import imgIm61 from "assets/images/produtoseservicos/im61.jpg";
import imgMolde12 from "assets/images/produtoseservicos/molde-1-2.jpg";
import imgMolde22 from "assets/images/produtoseservicos/molde-2-2.jpg";
import imgPeca2 from "assets/images/produtoseservicos/peca2.jpg";
import img12 from 'assets/images/produtoseservicos/WhatsApp Image 2025-09-15 at 22.21.15.jpeg'
import img13 from 'assets/images/produtoseservicos/WhatsApp Image 2025-09-15 at 22.21.152.jpeg'

/* paleta */
const BRAND = {
  main: "#036D3D",
  dark: "#004C2A",
  glass: "rgba(1,43,24,.58)",
  line: "rgba(3,109,61,.30)",
};

/* helpers locais */
function SectionContainer({ children, sx }) {
  return (
    <Container sx={{ maxWidth: 1100, width: "100%", mx: "auto", ...sx }}>
      {children}
    </Container>
  );
}
SectionContainer.propTypes = { children: PropTypes.node, sx: PropTypes.object };

const FadeIn = ({ children, show, timeout = 400, sx }) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (show) {
      const t = setTimeout(() => setVisible(true), 50);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [show]);
  return (
    <MKBox
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        transition: `opacity ${timeout}ms ease, transform ${timeout}ms ease`,
        ...sx,
      }}
    >
      {children}
    </MKBox>
  );
};
FadeIn.propTypes = { children: PropTypes.node, show: PropTypes.bool, timeout: PropTypes.number, sx: PropTypes.object };

export default function Servicos() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const parallax = isMobile ? "scroll" : "fixed";

  const [loaded, setLoaded] = useState(false);
  const NAVBAR_PT = { xs: 18, md: 25 };

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 120);
    return () => clearTimeout(t);
  }, []);

  const GALERIA = [
    img12, img13, imgMolde12, imgMolde22, imgPeca2, img01_1, img02_2, img03_1,
    img05_05, img06_06, img011,
    img15, img17, img032, imgDour,
    imgIm5, imgIm61,
  ];

  return (
    <>
      {/* HERO */}
      <main role="main">
        <MKBox
          role="img"
          aria-label="Usinagem de Precisão, Ferramentaria, Dispositivos e Moldes – Ramlis Machine"
          sx={{
            position: "relative",
            pt: NAVBAR_PT,
            minHeight: { xs: 600, md: 500 },
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            color: "#fff",
            backgroundImage: `url(${bgBanner})`,
            backgroundSize: "cover",
            backgroundPosition: { xs: "center", md: "center 35%" },
            backgroundAttachment: { xs: "scroll", md: parallax },
            "@media (prefers-reduced-motion: reduce)": { backgroundAttachment: "scroll" },
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0, 14, 8, 0.75) 0%, rgba(0,76,42,.55) 45%, rgba(0,76,42,.75) 100%)",
            },
          }}
        >
          <FadeIn show={loaded} timeout={600} sx={{ position: "relative", zIndex: 1, width: "100%" }}>
            <SectionContainer>
              <MKBox
                sx={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 0.5,
                  px: { xs: 1.5, md: 3 },
                  py: { xs: 1, md: 1.25 },
                  mx: "auto",
                  borderRadius: 2,
                  backgroundColor: BRAND.glass,
                  backdropFilter: "saturate(1.1) blur(3px)",
                  boxShadow: "0 10px 28px rgba(0,76,42,.35)",
                }}
              >
                <MKTypography
                  component="h1"
                  variant="h5"
                  fontWeight="bold"
                  color="white"
                  mb={0.5}
                  sx={{ textShadow: "0 2px 6px rgba(0,76,42,.85)" }}
                >
                  Usinagem, dispositivos de fixação, moldes e serviços de ferramentaria em todo o Brasil.
                </MKTypography>

                <MKTypography
                  variant="body1"
                  color="white"
                  sx={{ opacity: 0.95, textShadow: "0 1px 3px rgba(0,76,42,.8)" }}
                >
                  Especialistas em usinagem, fabricação de dispositivos de fixação e desenvolvimento de moldes,
                  com foco em qualidade, tolerâncias rigorosas, rapidez e alta repetibilidade.
                </MKTypography>

              </MKBox>
            </SectionContainer>
          </FadeIn>
        </MKBox>

        {/* Breadcrumbs */}
        <SectionContainer sx={{ mt: -3, mb: 4 }}>
          <Breadcrumbs
            routes={[{ label: "Home", route: "/" }, { label: "Serviços" }]}
            sx={{ "& .MuiBreadcrumbs-separator": { color: BRAND.main } }}
          />
        </SectionContainer>

        {/* Conteúdo principal */}
        <Card sx={{ p: 2, mx: { xs: 2, lg: 3 }, backgroundColor: "#fff", mt: -8, mb: 4, boxShadow: ({ boxShadows: { xxl } }) => xxl }}>
          <FadeIn show={loaded} timeout={700}>
            <SectionContainer sx={{ py: { xs: 3, md: 5 } }}>
              {/* Intro */}
              <Grid container spacing={{ xs: 3, md: 4 }}>
                <Grid item xs={12} md={7}>
                  <section aria-labelledby="servicos-usinagem">
                    <MKTypography component="h2" variant="h5" fontWeight="bold" sx={{ color: BRAND.dark, mb: 1 }}>
                      Serviços de usinagem de precisão
                    </MKTypography>
                    <MKTypography variant="body2" color="text" paragraph sx={{ lineHeight: 1.85, textAlign: "justify" }}>
                      Realizamos serviços completos de usinagem de precisão, dispositivos, moldes e ferramentaria, atendendo empresas em todo o Brasil.
                      Desenvolvemos projetos personalizados conforme as necessidades do cliente, garantindo tolerâncias rigorosas,
                      alta repetibilidade, qualidade dimensional e acabamento superior.
                    </MKTypography>
                  </section>
                  <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.85, textAlign: "justify" }}>
                    Com produção de alto nível e otimização dos processos, investimos constantemente em novos
                    equipamentos e na qualificação da equipe. Atendemos empresas nacionais e multinacionais
                    que fazem parte de nossa carteira de clientes.
                  </MKTypography>

                  <MKBox sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Chip icon={<PrecisionManufacturingIcon />} label="CNC" variant="outlined" />
                    <Chip icon={<BuildIcon />} label="Ferramentaria" variant="outlined" />
                    <Chip icon={<StraightenIcon />} label="Metrologia" variant="outlined" />
                    <Chip icon={<TuneIcon />} label="Acabamentos" variant="outlined" />
                    <Chip icon={<EngineeringIcon />} label="Projetos" variant="outlined" />
                  </MKBox>
                </Grid>

                {/* >>> VÍDEO NO LUGAR DAS “PRINCIPAIS CAPACIDADES” <<< */}
                <Grid item xs={12} md={5}>
                  <Card sx={{ p: 2, borderRadius: 3, border: `1px solid ${BRAND.line}`, overflow: "hidden" }}>
                    <MKBox
                      component="video"
                      src={produtoVideo}
                      controls
                      playsInline
                      title="Dispositivo pneumático – usinagem CNC – Ramlis Machine"
                      aria-label="Vídeo demonstrativo de dispositivo pneumático usinado"
                      style={{ width: "100%", height: "auto", borderRadius: 12, display: "block" }}
                    />
                    <MKTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
                      Teste de dispositivo pneumático — Área Cosmética
                    </MKTypography>
                  </Card>
                </Grid>
              </Grid>

              {/* Linha separadora */}
              <Divider
                sx={{
                  my: { xs: 4, md: 6 },
                  height: 2,
                  border: "none",
                  background: "linear-gradient(90deg, rgba(3,109,61,0) 0%, rgba(3,109,61,.35) 25%, rgba(3,109,61,.5) 50%, rgba(3,109,61,.35) 75%, rgba(3,109,61,0) 100%)",
                }}
              />

              {/* Carrossel logo após a linha */}
              <MKTypography component="h2" variant="h5" fontWeight="bold" sx={{ color: BRAND.dark, mb: 1 }}>
                Produtos / Peças fabricadas
              </MKTypography>
              <MKTypography variant="body2" color="text" paragraph sx={{ lineHeight: 1.85 }}>
                Especialistas em usinagem de precisão, fabricação de dispositivos de fixação e desenvolvimento de moldes industriais,
                com foco em qualidade, tolerâncias rigorosas, rapidez e alta repetibilidade.
              </MKTypography>

              <ProductsCarousel images={GALERIA} />

              {/* Linha separadora antes das máquinas */}
              <Divider
                sx={{
                  my: { xs: 4, md: 6 },
                  height: 2,
                  border: "none",
                  background: "linear-gradient(90deg, rgba(3,109,61,0) 0%, rgba(3,109,61,.35) 25%, rgba(3,109,61,.5) 50%, rgba(3,109,61,.35) 75%, rgba(3,109,61,0) 100%)",
                }}
              />

              {/* Máquinas com card de “Principais capacidades” na lateral */}
              <Grid container spacing={3}>
                <ServicesAccordions />
              </Grid>
            </SectionContainer>
          </FadeIn>
        </Card>

        <WhatsAppFloat />
      </main>
    </>
  );
}
